import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';
import { gameCellArtwork } from './36-game-artwork.mjs';

// Presentation maps conserved source values to the already accepted destinations.
// It never chooses merges, spawns, scores or moves; inconsistent snapshots fall
// back to the native accepted board instead of inventing a plausible animation.
function travelPlan(previous, accepted, events) {
  const before = previous.data.board;
  const after = accepted.data.board;
  if (!Array.isArray(before) || !Array.isArray(after) || before.length !== after.length)
    return null;
  if (accepted.gameId === 'tile-slide') {
    const to = before.indexOf(0),
      from = after.indexOf(0);
    if (from < 0 || to < 0 || from === to || before[from] !== after[to]) return null;
    if (Math.abs(Math.floor(from / 3) - Math.floor(to / 3)) + Math.abs((from % 3) - (to % 3)) !== 1)
      return null;
    if (before.some((value, index) => index !== from && index !== to && value !== after[index]))
      return null;
    return { moves: [{ from, to }], merges: [], spawn: [] };
  }
  const slide = events.find((event) => event.type === 'slide');
  if (!slide || !Number.isInteger(slide.direction) || slide.direction < 0 || slide.direction > 3)
    return null;
  const spawn = events
    .filter((event) => event.type === 'spawn')
    .flatMap((event) => event.cells || []);
  const columns = Math.sqrt(before.length);
  if (!Number.isInteger(columns) || columns < 2 || columns > 8) return null;
  const moves = [],
    merges = [];
  for (let lane = 0; lane < columns; lane++) {
    const indexes = Array.from({ length: columns }, (_, index) =>
      slide.direction < 2
        ? lane * columns + (slide.direction === 0 ? index : columns - 1 - index)
        : (slide.direction === 2 ? index : columns - 1 - index) * columns + lane,
    );
    const sources = indexes.filter((index) => before[index]);
    for (const to of indexes.filter((index) => after[index] && !spawn.includes(index))) {
      const group = [];
      let sum = 0;
      while (sum < after[to] && sources.length) {
        const from = sources.shift();
        group.push(from);
        sum += before[from];
      }
      if (
        sum !== after[to] ||
        group.length > 2 ||
        (group.length === 2 && before[group[0]] !== before[group[1]])
      )
        return null;
      moves.push(...group.map((from) => ({ from, to })));
      if (group.length === 2) merges.push(to);
    }
    if (sources.length) return null;
  }
  return { moves, merges, spawn };
}

/** Exact numeric travel above the accepted native board. Stable keyed buttons
 * retain input, labels and state; original artwork supplies the moving ghosts.
 * Cancellation releases all effects without replaying obsolete state. Hidden,
 * offscreen or reduced-motion interruption reveals accepted state immediately.
 * play accepts the existing events-array API or an earned amount plus settle.
 */
export function createNumberPlayback(ctx, board, { settle, onClear, visible = () => true } = {}) {
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animations = new Set(),
    ghosts = new Set(),
    awards = new Set();
  let generation = 0,
    disposed = false,
    current = null;
  function release() {
    for (const animation of animations) animation.cancel();
    for (const ghost of ghosts) ghost.remove();
    animations.clear();
    ghosts.clear();
    delete board.dataset.numberPlayback;
  }
  function cancel(reveal = false) {
    for (const award of awards) award.cancel();
    awards.clear();
    const interrupted = current;
    generation++;
    current = null;
    release();
    if (reveal && interrupted) setTilePicture(interrupted.state);
    else delete board.dataset.tileSolved;
    if (reveal && interrupted && !disposed && board.isConnected)
      interrupted.settle?.(interrupted.state);
  }
  function eligible() {
    return !disposed && board.isConnected && !document.hidden && !media.matches && visible();
  }
  function lifecycle() {
    if (!eligible()) cancel(true);
  }
  function cellAt(index) {
    return [...board.children].find((cell) => cell.dataset.cell === String(index));
  }
  function setTilePicture(state) {
    if (disposed || !board.isConnected) return;
    const values = state.data.board;
    const solved =
      state.gameId === 'tile-slide' &&
      Array.isArray(values) &&
      values.length === 9 &&
      values.every((value, index) => value === (index === 8 ? 0 : index + 1)) &&
      (state.data.stageComplete || state.completed);
    if (solved) board.dataset.tileSolved = 'true';
    else delete board.dataset.tileSolved;
  }
  function animate(node, frames, duration) {
    if (!node?.animate) return;
    const animation = node.animate(frames, {
      duration,
      fill: 'forwards',
      easing: 'cubic-bezier(.2,.8,.2,1)',
    });
    animations.add(animation);
  }
  async function wait() {
    const batch = [...animations];
    await Promise.all(
      batch.map((animation) => animation.finished?.catch(() => {}) ?? Promise.resolve()),
    );
    for (const animation of batch) {
      animation.cancel();
      animations.delete(animation);
    }
  }
  function ghost(previous, cell, index) {
    const target = cellAt(index),
      rect = target?.getBoundingClientRect(),
      origin = board.getBoundingClientRect();
    if (!rect || rect.width < 1 || rect.height < 1) return null;
    const node = ctx.e('span', 'game-number-flight');
    node.setAttribute('aria-hidden', 'true');
    Object.assign(node.style, {
      left: `${rect.left - origin.left}px`,
      top: `${rect.top - origin.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    });
    if (Number.isInteger(cell.colour))
      node.style.setProperty('--piece-colour', `var(--game-colour-${cell.colour})`);
    node.dataset.numberSource = String(index);
    node.append(gameCellArtwork(ctx, previous, cell, index, cell.glyph || '', String(cell.value)));
    ghosts.add(node);
    board.append(node);
    return node;
  }
  function mergeAward(accepted, previous, indexes) {
    const points = accepted.score - previous.score;
    if (points <= 0 || !indexes.length) return;
    const target = cellAt(indexes[0])?.getBoundingClientRect();
    const overlay = board.parentElement;
    const origin = overlay?.getBoundingClientRect();
    if (!target || !origin || origin.width < 1) return;
    const award = ctx.e('span', 'game-merge-award');
    award.setAttribute('aria-hidden', 'true');
    award.dataset.numberAward = String(points);
    award.textContent = `+${points}${indexes.length > 1 ? ` · ${indexes.length} MERGES` : ''}`;
    Object.assign(award.style, {
      left: `${Math.max(0, Math.min(origin.width - 128, target.left - origin.left + target.width / 2 - 64))}px`,
      top: `${target.top - origin.top + target.height / 2 - 18}px`,
    });
    overlay.append(award);
    const animation = award.animate(
      [
        { opacity: 1, transform: 'translateY(0) scale(.9)' },
        { opacity: 1, transform: 'translateY(-8px) scale(1)', offset: 0.72 },
        { opacity: 0, transform: 'translateY(-28px) scale(1.08)' },
      ],
      { duration: 950, fill: 'forwards', easing: 'ease-out' },
    );
    const owner = {
      cancel() {
        animation.cancel();
        award.remove();
        awards.delete(owner);
      },
    };
    awards.add(owner);
    void animation.finished.then(
      () => owner.cancel(),
      () => owner.cancel(),
    );
  }
  function play(previous, accepted, eventsOrEarned, override) {
    cancel();
    if (
      !['soft-merge', 'tile-slide'].includes(accepted.gameId) ||
      previous.gameId !== accepted.gameId ||
      !eligible() ||
      typeof cellAt(0)?.animate !== 'function'
    ) {
      setTilePicture(accepted);
      return null;
    }
    const events = Array.isArray(eventsOrEarned) ? eventsOrEarned : gameView(accepted).events || [];
    const plan = travelPlan(previous, accepted, events);
    if (!plan || !plan.moves.some((move) => move.from !== move.to)) return null;
    // Capture the validated pointer release before the input owner clears it.
    // Travel begins at the finger, preserving the tile's direct manipulation.
    const drag = board.gameDragTile;
    const dragFrom =
      previous.gameId === 'tile-slide' &&
      drag?.seed === previous.seed &&
      drag.moves === previous.moves
        ? drag
        : null;
    const token = generation;
    const finish = override || settle;
    current = { state: accepted, settle: finish };
    const valid = () => token === generation && eligible();
    return (async () => {
      try {
        const cells = gameView(previous).cells;
        const pieces = cells.map((cell, index) => ghost(previous, cell, index));
        if (pieces.some((piece) => !piece)) return;
        board.dataset.numberPlayback = 'true';
        for (const { from, to } of plan.moves) {
          if (from === to) continue;
          const source = cellAt(from).getBoundingClientRect(),
            destination = cellAt(to).getBoundingClientRect();
          pieces[from].style.zIndex = '4';
          pieces[from].dataset.numberDestination = String(to);
          animate(
            pieces[from],
            [
              {
                transform:
                  dragFrom?.from === from
                    ? `translate(${dragFrom.dx}px,${dragFrom.dy}px)`
                    : 'translate(0,0)',
              },
              {
                transform: `translate(${destination.left - source.left}px,${destination.top - source.top}px)`,
              },
            ],
            280,
          );
        }
        await wait();
        if (!valid()) return;
        for (const piece of ghosts) piece.remove();
        ghosts.clear();
        delete board.dataset.numberPlayback;
        setTilePicture(accepted);
        if (accepted.gameId === 'tile-slide' && board.dataset.tileSolved === 'true') {
          animate(
            board,
            [
              { filter: 'brightness(1)' },
              { filter: 'brightness(1.16)' },
              { filter: 'brightness(1)' },
            ],
            280,
          );
        }
        if (accepted.gameId === 'soft-merge') mergeAward(accepted, previous, plan.merges);
        for (const index of plan.merges)
          animate(
            cellAt(index)?.firstChild,
            [
              { transform: 'scale(1)' },
              { transform: 'scale(1.16)', offset: 0.4 },
              { transform: 'scale(1)' },
            ],
            190,
          );
        for (const index of plan.spawn)
          animate(
            cellAt(index)?.firstChild,
            [
              { opacity: 0, transform: 'scale(.7)' },
              { opacity: 1, transform: 'scale(1)' },
            ],
            190,
          );
        if (plan.merges.length)
          onClear?.({
            type: 'merge',
            cells: [...plan.merges],
            points: accepted.score - previous.score,
          });
        await wait();
      } catch {
        // Graphics are optional. The accepted native board remains intact.
      } finally {
        if (token === generation) {
          current = null;
          release();
          if (!disposed && board.isConnected) finish?.(accepted);
        }
      }
    })();
  }
  document.addEventListener('visibilitychange', lifecycle);
  media.addEventListener?.('change', lifecycle);
  return {
    play,
    cancel,
    dispose() {
      if (disposed) return;
      disposed = true;
      cancel();
      document.removeEventListener('visibilitychange', lifecycle);
      media.removeEventListener?.('change', lifecycle);
    },
  };
}
